import { useLocation, useNavigate } from 'react-router';
import { getSupportCaseDescription } from '../../components/FixedBroadbandCheckout/FixedBroadbandSupportCaseUtils';
import { SupportCase } from '../../generated/api/supportCase';
import { googleEcommercePurchase } from '../../selfservice/actions';
import { clearFixedBroadbandOrderDraft } from '../../selfservice/common/fixedBroadbandOrderStorage.js';
import { PaymentTypeEnum, getProductPriceData } from '../../selfservice/common/googleEcommerceEvent';
import { pushToDataLayer } from '../analytics';
import { paths } from '../constants/pathVariables';
import { createBillingAccount, createSupportCaseAuthenticated } from '../fetch';
import { useSearchParams } from './useSearchParams';
import type { FixedBroadbandOrder } from '../../components/FixedBroadbandCheckout/FixedBroadbandSupportCaseUtils';
import type { BillingAccount } from '../../generated/api/billingAccount';
import type { EcommerceItem } from '../analytics';
import type { ConfiguredOffer } from '../types/commercialProduct';

const createBaAndUpdateOrder = async (
  order: FixedBroadbandOrder,
  mdmId: string,
  newBa?: BillingAccount
): Promise<FixedBroadbandOrder> => {
  if (newBa) {
    const baResp = await createBillingAccount(newBa, mdmId);
    return {
      ...order,
      billingAccountDisplayId: baResp.billingAccountDisplayId,
    };
  } else {
    return order;
  }
};

export const useSubmitFbbOrder = () => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return async (
    order: FixedBroadbandOrder,
    configuredOffer: ConfiguredOffer,
    eCommerceEventProducts: EcommerceItem[],
    newBa?: BillingAccount
  ) => {
    const orderWithBa = await createBaAndUpdateOrder(order, mdmId!, newBa);

    const payload: SupportCase = {
      feature: SupportCase.FeatureEnum.FIXED_BROADBAND_ORDER,
      action: 'Kiinteän laajakaistan tilaus / saatavuuskysely',
      url: pathname,
      description: getSupportCaseDescription(orderWithBa),
    };

    const supportCaseResp = await createSupportCaseAuthenticated(payload, mdmId!);
    clearFixedBroadbandOrderDraft();
    const { totalTax, totalRevenue, totalShipping } = getProductPriceData([configuredOffer]);

    pushToDataLayer(
      googleEcommercePurchase(
        supportCaseResp.supportCaseDisplayId || '',
        totalRevenue,
        totalTax,
        totalShipping,
        eCommerceEventProducts,
        PaymentTypeEnum.INVOICE
      ).event
    );

    navigate(paths.ANONYMOUS_FIXED_BROADBAND_ORDER_CONFIRMATION);
  };
};
